import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CheckCircle2, UserPlus, Zap, FileText, Loader2, EyeOff, Building2, User } from "lucide-react";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { apiRequest } from "@/lib/queryClient";
import { getLocalizedInvoiceDetailUrl, getLocalizedUrl } from "@/lib/localized-routes";
import { ClientForm } from "@/components/client-form";
import { QuickTaskModal, type QuickTaskInitialAction } from "@/components/quick-task-modal";
import type { InsertClient, Client } from "@shared/schema";

const COUNTRIES = ["Slovakia", "Czech Republic", "Austria", "Poland", "Hungary", "Germany", "United Kingdom", "United States"].sort();

interface OnboardingStatus {
  hasClients: boolean;
  hasTasks: boolean;
  hasInvoices: boolean;
  dismissed?: boolean;
}

interface OnboardingChecklistProps {
  onDismiss?: () => void;
}

function hasMinimalInvoiceData(user: { companyStreet?: string; companyCity?: string; companyPostalCode?: string; companyCountry?: string } | null): boolean {
  if (!user?.companyStreet?.trim()) return false;
  if (!user.companyCity?.trim()) return false;
  if (!user.companyPostalCode?.trim()) return false;
  if (!user.companyCountry?.trim()) return false;
  return true;
}

export function OnboardingChecklist({ onDismiss }: OnboardingChecklistProps) {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [onboardingStep, setOnboardingStep] = useState<1 | 2>(1);
  const [isAddClientOpen, setIsAddClientOpen] = useState(false);
  const [isQuickTaskOpen, setIsQuickTaskOpen] = useState(false);
  const [quickTaskInitialAction, setQuickTaskInitialAction] = useState<QuickTaskInitialAction>("invoice");
  const [lastCreatedClientId, setLastCreatedClientId] = useState<number | null>(null);

  // Step 1 form state
  const [invoiceAsCompany, setInvoiceAsCompany] = useState(true);
  const [step1Form, setStep1Form] = useState({
    companyName: "",
    ico: "",
    dic: "",
    icDph: "",
    firstName: "",
    lastName: "",
    street: "",
    city: "",
    postalCode: "",
    country: "Slovakia",
    email: "",
  });

  // Init step1 form from user
  useEffect(() => {
    if (user) {
      setStep1Form((p) => ({
        ...p,
        companyName: (user as any).companyName || (user as any).company_name || "",
        ico: (user as any).companyRegNumber || (user as any).company_reg_number || "",
        dic: (user as any).companyTaxId || (user as any).company_tax_id || "",
        icDph: (user as any).companyVatId || (user as any).company_vat_id || "",
        firstName: (user as any).firstName || (user as any).first_name || "",
        lastName: (user as any).lastName || (user as any).last_name || "",
        street: (user as any).companyStreet || (user as any).company_street || "",
        city: (user as any).companyCity || (user as any).company_city || "",
        postalCode: (user as any).companyPostalCode || (user as any).company_postal_code || "",
        country: (user as any).companyCountry || (user as any).company_country || "Slovakia",
        email: (user as any).email || "",
      }));
    }
  }, [user?.id]);

  const { data: status, isLoading, error } = useQuery<OnboardingStatus>({
    queryKey: ["/api/onboarding/status"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/onboarding/status");
      if (!res.ok) throw new Error("Failed to fetch onboarding status");
      return res.json();
    },
    retry: false,
    staleTime: 30_000,
  });

  const { data: clients } = useQuery<Client[]>({
    queryKey: ["/api/clients"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/clients");
      const data = await res.json();
      return data.items || data || [];
    },
    enabled: !!status && !status.dismissed,
  });

  const { data: invoicesData } = useQuery({
    queryKey: ["/api/invoices"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/invoices?limit=1");
      const data = await res.json();
      return data;
    },
    enabled: !!status && status.hasInvoices,
  });

  const dismissMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/onboarding/dismiss");
      if (!res.ok) throw new Error("Failed to dismiss");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/onboarding/status"] });
      onDismiss?.();
    },
    onError: () => {
      toast({ title: "Chyba", description: "Nepodarilo sa skryť onboarding.", variant: "destructive" });
    },
  });

  const saveProfileMutation = useMutation({
    mutationFn: async (data: Record<string, string>) => {
      const res = await apiRequest("PATCH", "/api/user/profile", data);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || err.error || "Nepodarilo sa uložiť.");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/user"] });
    },
    onError: (e: Error) => {
      toast({ title: "Chyba", description: e.message, variant: "destructive" });
    },
  });

  const createClientMutation = useMutation({
    mutationFn: async (data: InsertClient) => {
      const res = await apiRequest("POST", "/api/clients", data);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || err.error || "Nepodarilo sa vytvoriť klienta.");
      }
      return res.json();
    },
    onSuccess: (created: { id?: number }) => {
      setIsAddClientOpen(false);
      setLastCreatedClientId(created?.id ?? null);
      queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
      queryClient.invalidateQueries({ queryKey: ["/api/onboarding/status"] });
      toast({ title: "Klient pridaný", description: "Vytvorte rýchlu úlohu a faktúru." });
      setQuickTaskInitialAction("invoice");
      setIsQuickTaskOpen(true);
    },
    onError: (e: Error) => {
      toast({ title: "Chyba", description: e.message, variant: "destructive" });
    },
  });

  // Don't show if API failed, loading, or dismissed
  if (error || isLoading || !status || status.dismissed) return null;

  const allDone = status.hasClients && status.hasTasks && status.hasInvoices;
  const hasInvoiceData = hasMinimalInvoiceData(user);
  const showStep1 = !hasInvoiceData && onboardingStep === 1 && !allDone;

  const lastInvoice = invoicesData?.data?.[0];
  const lastInvoiceId = lastInvoice?.id ?? lastInvoice?.invoice_id;

  const handleStep1Next = async () => {
    const street = step1Form.street?.trim();
    const city = step1Form.city?.trim();
    const postalCode = step1Form.postalCode?.trim();
    const country = step1Form.country?.trim();
    if (!street || !city || !postalCode || !country) {
      toast({ title: "Chyba", description: "Vyplňte adresu (ulica, mesto, PSČ, krajina). Bez adresy nepôjde poslať faktúru emailom.", variant: "destructive" });
      return;
    }
    const payload: Record<string, string> = {
      companyStreet: street,
      companyCity: city,
      companyPostalCode: postalCode,
      companyCountry: country,
    };
    if (invoiceAsCompany) {
      payload.companyName = step1Form.companyName?.trim() || "";
      if (step1Form.ico?.trim()) payload.companyRegNumber = step1Form.ico.trim();
      if (step1Form.dic?.trim()) payload.companyTaxId = step1Form.dic.trim();
      if (step1Form.icDph?.trim()) payload.companyVatId = step1Form.icDph.trim();
    } else {
      payload.firstName = step1Form.firstName?.trim() || "";
      payload.lastName = step1Form.lastName?.trim() || "";
    }
    payload.companyName = invoiceAsCompany ? (step1Form.companyName?.trim() || "Firma") : `${step1Form.firstName || ""} ${step1Form.lastName || ""}`.trim() || "Fyzická osoba";
    await saveProfileMutation.mutateAsync(payload);
    setOnboardingStep(2);
  };

  const handleQuickTask = (action: QuickTaskInitialAction = "invoice") => {
    setQuickTaskInitialAction(action);
    setLastCreatedClientId(null);
    setIsQuickTaskOpen(true);
  };

  const handleQuickTaskSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/tasks"] });
    queryClient.invalidateQueries({ queryKey: ["/api/invoices"] });
    queryClient.invalidateQueries({ queryKey: ["/api/onboarding/status"] });
  };

  const handleFinishOpenInvoice = () => {
    dismissMutation.mutate();
    if (lastInvoiceId) {
      setLocation(getLocalizedInvoiceDetailUrl(lastInvoiceId));
    } else {
      setLocation(getLocalizedUrl("/invoices"));
    }
  };

  const handleFinishGoToTasks = () => {
    dismissMutation.mutate();
    setLocation(getLocalizedUrl("/tasks"));
  };

  return (
    <>
      <Card className="mb-6 sm:mb-8 border-primary/20 bg-primary/5">
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between gap-2">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Zap className="h-5 w-5 text-primary" />
                {showStep1 ? "Údaje na faktúru" : allDone ? "Hotovo" : "Prvý výsledok"}
              </CardTitle>
              <CardDescription>
                {showStep1
                  ? "Bez adresy a emailu nepôjde poslať faktúru emailom."
                  : allDone
                    ? "Prvá faktúra pripravená"
                    : "Klient → rýchla úloha → faktúra"}
              </CardDescription>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-foreground shrink-0"
              onClick={() => dismissMutation.mutate()}
              disabled={dismissMutation.isPending}
            >
              {dismissMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <EyeOff className="h-4 w-4" />}
              <span className="ml-1 hidden sm:inline">Preskočiť</span>
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {allDone ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-green-600 dark:text-green-500">
                <CheckCircle2 className="h-5 w-5" />
                <span className="font-medium">Hotovo – prvá faktúra pripravená</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button onClick={handleFinishOpenInvoice}>
                  <FileText className="h-4 w-4 mr-2" />
                  Otvoriť faktúru
                </Button>
                <Button variant="outline" onClick={handleFinishGoToTasks}>
                  Prejsť na úlohy
                </Button>
              </div>
            </div>
          ) : showStep1 ? (
            <div className="space-y-4">
              <div className="flex gap-2 p-1 rounded-lg bg-muted/50">
                <Button
                  variant={invoiceAsCompany ? "default" : "ghost"}
                  size="sm"
                  className="flex-1"
                  onClick={() => setInvoiceAsCompany(true)}
                >
                  <Building2 className="h-4 w-4 mr-1" />
                  Faktúrujem ako Firma
                </Button>
                <Button
                  variant={!invoiceAsCompany ? "default" : "ghost"}
                  size="sm"
                  className="flex-1"
                  onClick={() => setInvoiceAsCompany(false)}
                >
                  <User className="h-4 w-4 mr-1" />
                  Faktúrujem ako Fyzická osoba
                </Button>
              </div>
              <div className="space-y-3">
                {invoiceAsCompany ? (
                  <>
                    <div>
                      <Label>Názov firmy</Label>
                      <Input value={step1Form.companyName} onChange={(e) => setStep1Form((p) => ({ ...p, companyName: e.target.value }))} placeholder="Moja s.r.o." />
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <Label>IČO</Label>
                        <Input value={step1Form.ico} onChange={(e) => setStep1Form((p) => ({ ...p, ico: e.target.value }))} placeholder="12345678" />
                      </div>
                      <div>
                        <Label>DIČ (voliteľné)</Label>
                        <Input value={step1Form.dic} onChange={(e) => setStep1Form((p) => ({ ...p, dic: e.target.value }))} placeholder="SK1234567890" />
                      </div>
                      <div>
                        <Label>IČ DPH (voliteľné)</Label>
                        <Input value={step1Form.icDph} onChange={(e) => setStep1Form((p) => ({ ...p, icDph: e.target.value }))} placeholder="SK1234567890" />
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label>Meno</Label>
                      <Input value={step1Form.firstName} onChange={(e) => setStep1Form((p) => ({ ...p, firstName: e.target.value }))} placeholder="Ján" />
                    </div>
                    <div>
                      <Label>Priezvisko</Label>
                      <Input value={step1Form.lastName} onChange={(e) => setStep1Form((p) => ({ ...p, lastName: e.target.value }))} placeholder="Novák" />
                    </div>
                  </div>
                )}
                <div>
                  <Label>Ulica *</Label>
                  <Input value={step1Form.street} onChange={(e) => setStep1Form((p) => ({ ...p, street: e.target.value }))} placeholder="Hlavná 1" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>Mesto *</Label>
                    <Input value={step1Form.city} onChange={(e) => setStep1Form((p) => ({ ...p, city: e.target.value }))} placeholder="Bratislava" />
                  </div>
                  <div>
                    <Label>PSČ *</Label>
                    <Input value={step1Form.postalCode} onChange={(e) => setStep1Form((p) => ({ ...p, postalCode: e.target.value }))} placeholder="81101" />
                  </div>
                </div>
                <div>
                  <Label>Krajina *</Label>
                  <Select value={step1Form.country} onValueChange={(v) => setStep1Form((p) => ({ ...p, country: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Email pre faktúry</Label>
                  <Input type="email" value={step1Form.email} readOnly className="bg-muted" placeholder="Použije sa váš prihlasovací email" />
                  <p className="text-xs text-muted-foreground mt-1">Používa sa váš prihlasovací email</p>
                </div>
              </div>
              <Button onClick={handleStep1Next} disabled={saveProfileMutation.isPending}>
                {saveProfileMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Ďalej
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-4 p-3 rounded-lg border bg-background/50">
                <div className="flex items-center gap-3 min-w-0">
                  {status.hasClients ? (
                    <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
                  ) : (
                    <div className="w-5 h-5 rounded-full border-2 border-muted-foreground shrink-0" />
                  )}
                  <span className={status.hasClients ? "text-muted-foreground line-through" : ""}>1. Pridať klienta</span>
                </div>
                {!status.hasClients && (
                  <Button size="sm" onClick={() => setIsAddClientOpen(true)}>
                    <UserPlus className="h-4 w-4 mr-1" />
                    Pridať klienta
                  </Button>
                )}
              </div>
              <div className="flex items-center justify-between gap-4 p-3 rounded-lg border bg-background/50">
                <div className="flex items-center gap-3 min-w-0">
                  {status.hasTasks ? (
                    <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
                  ) : (
                    <div className="w-5 h-5 rounded-full border-2 border-muted-foreground shrink-0" />
                  )}
                  <span className={status.hasTasks ? "text-muted-foreground line-through" : ""}>2. Vytvoriť rýchlu úlohu</span>
                </div>
                {!status.hasTasks && (
                  <Button size="sm" onClick={() => handleQuickTask("invoice")} disabled={!status.hasClients}>
                    <Zap className="h-4 w-4 mr-1" />
                    Rýchla úloha
                  </Button>
                )}
              </div>
              <div className="flex items-center justify-between gap-4 p-3 rounded-lg border bg-background/50">
                <div className="flex items-center gap-3 min-w-0">
                  {status.hasInvoices ? (
                    <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
                  ) : (
                    <div className="w-5 h-5 rounded-full border-2 border-muted-foreground shrink-0" />
                  )}
                  <span className={status.hasInvoices ? "text-muted-foreground line-through" : ""}>3. Vytvoriť faktúru</span>
                </div>
                {!status.hasInvoices ? (
                  <div className="flex gap-2">
                    <Button size="sm" variant="default" onClick={() => handleQuickTask("invoice")} disabled={!status.hasClients || !status.hasTasks}>
                      <FileText className="h-4 w-4 mr-1" />
                      Uložiť a vytvoriť faktúru
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleQuickTask("invoice_and_send")} disabled={!status.hasClients || !status.hasTasks}>
                      Uložiť a odoslať emailom
                    </Button>
                  </div>
                ) : lastInvoiceId ? (
                  <Button size="sm" variant="outline" onClick={() => setLocation(getLocalizedInvoiceDetailUrl(lastInvoiceId))}>
                    Otvoriť faktúru
                  </Button>
                ) : null}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={isAddClientOpen} onOpenChange={setIsAddClientOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Pridať klienta</DialogTitle>
            <DialogDescription>
              Zadajte základné údaje klienta. Po uložení môžete vytvoriť rýchlu úlohu a faktúru.
            </DialogDescription>
          </DialogHeader>
          <ClientForm
            onSubmit={async (data) => {
              await createClientMutation.mutateAsync(data);
            }}
            isSubmitting={createClientMutation.isPending}
            onClose={() => setIsAddClientOpen(false)}
          />
        </DialogContent>
      </Dialog>

      <QuickTaskModal
        open={isQuickTaskOpen}
        onOpenChange={setIsQuickTaskOpen}
        clients={clients || []}
        defaultClientId={lastCreatedClientId ?? (clients?.[0]?.id)}
        defaultBillingMode="items"
        initialAction={quickTaskInitialAction}
        onCreateSuccess={handleQuickTaskSuccess}
      />
    </>
  );
}
